import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import HrLayout from "../HrLayout";
import { Link } from "react-router-dom";

const PAGE_SIZE = 10;

export default function HrEmployees() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  // filters
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [dept, setDept] = useState("all");

  // pagination
  const [page, setPage] = useState(1);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/hr/employees");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setError("Failed to load employees.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // departments dropdown from data
  const departments = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (r.department_id && r.department_name) {
        map.set(String(r.department_id), r.department_name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [rows]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();

    return rows.filter((r) => {
      const fullName = `${r.first_name || ""} ${r.last_name || ""}`.trim();
      const haystack = [
        fullName,
        r.email,
        r.phone,
        r.employee_code,
        r.designation,
        r.department_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchQ = !query || haystack.includes(query);

      const matchStatus =
        status === "all"
          ? true
          : String(r.status || "").toLowerCase() === status;

      const matchDept =
        dept === "all" ? true : String(r.department_id || "") === String(dept);

      return matchQ && matchStatus && matchDept;
    });
  }, [rows, q, status, dept]);

  const totalPages = Math.max(Math.ceil(filtered.length / PAGE_SIZE), 1);

  const pageData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  // reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [q, status, dept]);

  const chipClass = (val) => {
    const s = String(val || "").toLowerCase();
    if (s === "active")
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    return "bg-rose-50 text-rose-700 border-rose-200";
  };

  return (
    <HrLayout title="Employees">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Employees</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage employees, filter and view details.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={load}
            className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Refresh
          </button>

          {/* Future: add employee */}
          <Link
            to="/hr/employees/add"
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
          >
            + Add Employee
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-6">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Search
            </label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, code, department…"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Department
            </label>
            <select
              value={dept}
              onChange={(e) => setDept(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
            >
              <option value="all">All</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200">
            Total: <b className="text-slate-700">{rows.length}</b>
          </span>
          <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200">
            Filtered: <b className="text-slate-700">{filtered.length}</b>
          </span>
          <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200">
            Page: <b className="text-slate-700">{page}</b> / {totalPages}
          </span>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr>
                <Th>Employee</Th>
                <Th>Code</Th>
                <Th>Email</Th>
                <Th>Department</Th>
                <Th>Designation</Th>
                <Th>Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <Td>
                      <Skeleton />
                    </Td>
                    <Td>
                      <Skeleton />
                    </Td>
                    <Td>
                      <Skeleton />
                    </Td>
                    <Td>
                      <Skeleton />
                    </Td>
                    <Td>
                      <Skeleton />
                    </Td>
                    <Td>
                      <Skeleton />
                    </Td>
                    <Td className="text-right">
                      <Skeleton />
                    </Td>
                  </tr>
                ))
              ) : pageData.length === 0 ? (
                <tr>
                  <Td colSpan={7} className="text-center py-10 text-slate-500">
                    No employees found for current filters.
                  </Td>
                </tr>
              ) : (
                pageData.map((e) => {
                  const name =
                    `${e.first_name || ""} ${e.last_name || ""}`.trim() || "—";
                  return (
                    <tr
                      key={e.id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                    >
                      <Td>
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">
                            {name !== "—" ? name[0].toUpperCase() : "E"}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-800 truncate">
                              {name}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              {e.phone || "—"}
                            </div>
                          </div>
                        </div>
                      </Td>
                      <Td className="font-medium text-slate-700">
                        {e.employee_code || "—"}
                      </Td>
                      <Td>{e.email || "—"}</Td>
                      <Td>{e.department_name || "—"}</Td>
                      <Td>{e.designation || "—"}</Td>
                      <Td>
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold ${chipClass(
                            e.status
                          )}`}
                        >
                          {(e.status || "inactive").toString().toUpperCase()}
                        </span>
                      </Td>
                      <Td className="text-right">
                        <div className="inline-flex gap-2">
                          <Link
                            to={`/hr/employees/${e.id}`}
                            className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            View
                          </Link>
                          <button
                            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700"
                            onClick={() =>
                              alert("Edit modal / page will be added next.")
                            }
                          >
                            Edit
                          </button>
                        </div>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              Showing{" "}
              <b className="text-slate-700">{(page - 1) * PAGE_SIZE + 1}</b> to{" "}
              <b className="text-slate-700">
                {Math.min(page * PAGE_SIZE, filtered.length)}
              </b>{" "}
              of <b className="text-slate-700">{filtered.length}</b>
            </p>

            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold disabled:opacity-50 hover:bg-slate-50"
              >
                Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold disabled:opacity-50 hover:bg-slate-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </HrLayout>
  );
}

/* UI helpers */
function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500 ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "", colSpan }) {
  return (
    <td
      colSpan={colSpan}
      className={`px-4 py-3 text-slate-700 align-middle ${className}`}
    >
      {children}
    </td>
  );
}
function Skeleton() {
  return <div className="h-4 w-full bg-slate-100 rounded animate-pulse" />;
}
